var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Card, CardActions, CardContent, CardHeader, Container, LinearProgress, Link, Stack, TextField, ThemeProvider, Typography, } from "@mui/material";
import { AddAPhoto, AddLink, SaveAlt } from "@mui/icons-material";
import Heading from "../Heading";
import Footer from "../Footer";
import getTheme from "../Theme";
import { getLayoutInfo } from "../LayoutInfo";
import { restoreSession, useProfileAuthStore } from "../store/ProfileStore";
import { loadChannelDataFromURL, DATA_UTIL_CLIENT_ERROR_LOAD_DATA_FAILED_SESSION_EXPIRED, } from "../util/DataUtils";
import { getContentURL } from "../ContentAPI";
import { authUrl, useCacheBusterStore } from "../util/UrlUtils";
import { useLogger } from "../logger";
import { forceLogin } from "../Auth/AuthUtils";
import { useFileUploader } from "../hooks/useFileUploader";
import { isVideoFile } from "../util/ImageUtils";
import { getConvertedVideoUrl } from "../util/VideoUtils";
import { ContentCard } from "./ContentCard";
import { UserAvatar } from "./UserAvatar";
import { savePost as savePostShared, } from "../util/DataUtils";
const LOG_PREFIX = "EditPost: ";
// Interfaces removed - now using unified savePost operation in content lambda
export const EditPost = () => {
    // Basic post loading state
    const [postData, setPostData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState(null);
    // Form state - will be initialized with existing post data
    const [postTitle, setPostTitle] = useState("");
    const [postTitleHelper, setPostTitleHelper] = useState(undefined);
    const [postSubtitle, setPostSubtitle] = useState("");
    const [postSubtitleHelper, setPostSubtitleHelper] = useState(undefined);
    const [postText, setPostText] = useState("");
    const [postTextHelper, setPostTextHelper] = useState(undefined);
    const [postLink, setPostLink] = useState("");
    const [postLinkHelper, setPostLinkHelper] = useState(undefined);
    // Simple file state
    const [tempFiles, setTempFiles] = useState([]);
    const [previewImageDataUrl, setPreviewImageDataUrl] = useState("");
    const [uploadingFiles, setUploadingFiles] = useState(false);
    const [allFilesUploaded, setAllFilesUploaded] = useState(false);
    // UI state
    const [addLinkVisible, setAddLinkVisible] = useState(false);
    const [saving, setSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    // Existing media for edit mode (simple approach)
    const [existingMediaUrl, setExistingMediaUrl] = useState("");
    const [existingIsVideo, setExistingIsVideo] = useState(false);
    // Simple render trigger like NewPostCard
    const [forceRender, setForceRender] = useState(0);
    const location = useLocation();
    const navigate = useNavigate();
    const auth = useProfileAuthStore((state) => state);
    const cacheBusterStore = useCacheBusterStore((state) => state);
    const logger = useLogger((state) => state);
    const layout = getLayoutInfo();
    // Refs
    const canvasRef = useRef(null);
    const hiddenFileInput = useRef(null);
    // File uploader hook
    const fileUploader = useFileUploader({
        maxImageSize: 50 * 1024 * 1024,
        maxVideoSize: 1024 * 1024 * 1024,
        acceptedFileTypes: [
            "image/jpeg",
            "image/png",
            "video/mp4",
            "video/quicktime",
        ],
        onUploadError: (error) => {
            logger.error(LOG_PREFIX + "Upload error: " + error.message);
            setErrorMessage(error.message);
            setUploadingFiles(false);
        },
    });
    // Extract parameters from URL
    const queryParams = new URLSearchParams(location.search);
    const postId = queryParams.get("postId");
    const channelOwnerId = queryParams.get("channelOwnerId");
    const channelId = queryParams.get("channelId");
    // Simple session restoration - no excessive checks
    useEffect(() => {
        if (!auth.isSignedIn() && !restoreSession(auth, logger)) {
            logger.log(LOG_PREFIX + "User NOT logged in");
        }
    }, []);
    // Load post data once
    useEffect(() => {
        if (!postId || !channelOwnerId || !channelId || !(auth === null || auth === void 0 ? void 0 : auth.isSignedIn())) {
            if (!postId || !channelOwnerId || !channelId) {
                setErrorMessage("Missing required parameters: postId, channelOwnerId, or channelId");
                setLoading(false);
            }
            return;
        }
        const postJsonUrl = `${getContentURL()}/u/${channelOwnerId}/${channelId}/${postId}.json`;
        logger.log(LOG_PREFIX + "Loading post data from: " + postJsonUrl);
        loadChannelDataFromURL(postJsonUrl, {
            okCallback: (clientDetailCode, data) => {
                try {
                    const parsedData = JSON.parse(data);
                    if (parsedData.postOwnerId !== auth.userId &&
                        auth.accountType !== "admin") {
                        setErrorMessage("You don't have permission to edit this post");
                        setLoading(false);
                        return;
                    }
                    setPostData(parsedData);
                    // Initialize form fields
                    setPostTitle(parsedData.title || "");
                    setPostSubtitle(parsedData.subtitle || "");
                    setPostText(parsedData.text || "");
                    setPostLink(parsedData.link || "");
                    setAddLinkVisible(!!parsedData.link);
                    // Load existing media if present (simple approach)
                    if (parsedData.hasImage || parsedData.hasVideo) {
                        const postUrl = `${getContentURL()}/u/${channelOwnerId}/${channelId}/${parsedData.postId}`;
                        if (parsedData.hasVideo) {
                            const videoBaseUrl = `${postUrl}.mp4`;
                            const hlsVideoUrl = getConvertedVideoUrl(videoBaseUrl);
                            const videoUrl = authUrl(hlsVideoUrl, cacheBusterStore, auth, logger);
                            setExistingMediaUrl(videoUrl);
                            setExistingIsVideo(true);
                        }
                        else if (parsedData.hasImage) {
                            const imageUrl = parsedData.hasMultipleFiles && parsedData.multipleFileCount
                                ? authUrl(`${postUrl}_1_512.jpg`, cacheBusterStore, auth, logger)
                                : authUrl(`${postUrl}_512.jpg`, cacheBusterStore, auth, logger);
                            setExistingMediaUrl(imageUrl);
                            setExistingIsVideo(false);
                        }
                    }
                    setLoading(false);
                }
                catch (err) {
                    logger.error(LOG_PREFIX + "Failed to parse post data: " + err);
                    setErrorMessage("Failed to parse post data: " + err.message);
                    setLoading(false);
                }
            },
            errorCallback: (clientDetailCode, errorMessage) => {
                if (clientDetailCode ===
                    DATA_UTIL_CLIENT_ERROR_LOAD_DATA_FAILED_SESSION_EXPIRED) {
                    forceLogin(navigate);
                    return;
                }
                setErrorMessage(errorMessage.includes("NoSuchKey")
                    ? "Post not found. It may have been deleted or the URL is incorrect."
                    : errorMessage + " (" + clientDetailCode + ")");
                setLoading(false);
            },
        }, cacheBusterStore, logger);
    }, [postId, channelOwnerId, channelId, auth === null || auth === void 0 ? void 0 : auth.userId]);
    // Handle file selection - using NewPostCard's successful pattern
    const handleFileSelection = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c;
        if (!((_b = (_a = hiddenFileInput.current) === null || _a === void 0 ? void 0 : _a.files) === null || _b === void 0 ? void 0 : _b.length))
            return;
        const files = Array.from(hiddenFileInput.current.files);
        logger.log(LOG_PREFIX + "handleFileSelection: " + files.length + " files selected");
        // Mark changes and clear errors
        setHasChanges(true);
        setErrorMessage(null);
        // Validate files
        const invalidFiles = files.filter((file) => file.type !== "image/jpeg" &&
            file.type !== "image/png" &&
            file.type !== "video/mp4" &&
            file.type !== "video/quicktime");
        if (invalidFiles.length > 0) {
            setErrorMessage("Unsupported file type(s): " +
                invalidFiles.map((f) => f.type).join(", "));
            return;
        }
        if (!auth.userId) {
            setErrorMessage("User ID not found. Please sign in again.");
            return;
        }
        // Generate temp path with timestamp
        const timestamp = new Date().getTime();
        const tempPartition = `tmp/${auth.userId}/${timestamp}`;
        // Create TempFile entries
        const newTempFiles = files.map((file, index) => {
            let fileType = "jpg";
            if (file.type === "image/jpeg")
                fileType = "jpg";
            else if (file.type === "image/png")
                fileType = "png";
            else if (file.type === "video/mp4")
                fileType = "mp4";
            else if (file.type === "video/quicktime")
                fileType = "mov";
            const tempContentId = `temp_${index}_${timestamp}`;
            const actualS3Key = `${tempPartition}/${tempContentId}.${fileType}`;
            return {
                file,
                tempPath: actualS3Key,
                filename: file.name,
                fileType,
                uploadProgress: 0,
                uploadComplete: false,
            };
        });
        // Clear existing media
        setExistingMediaUrl("");
        setExistingIsVideo(false);
        // Handle first file for preview (using NewPostCard pattern)
        const firstFile = (_c = newTempFiles[0]) === null || _c === void 0 ? void 0 : _c.file;
        if (firstFile && !isVideoFile(firstFile)) {
            // Create blob URL for immediate preview - this is the key!
            const previewUrl = URL.createObjectURL(firstFile);
            logger.log(LOG_PREFIX + "Image preview URL created: " + previewUrl);
            setPreviewImageDataUrl(previewUrl);
            setTempFiles(newTempFiles);
            setUploadingFiles(true);
            setAllFilesUploaded(false);
            setForceRender((prev) => prev + 1); // Trigger re-render
            startFileUploads(newTempFiles, tempPartition, timestamp);
        }
        else if (firstFile && isVideoFile(firstFile)) {
            // Video handling
            setPreviewImageDataUrl("");
            setTempFiles(newTempFiles);
            setUploadingFiles(true);
            setAllFilesUploaded(false);
            setForceRender((prev) => prev + 1);
            startFileUploads(newTempFiles, tempPartition, timestamp);
        }
    });
    // Multi-file uploads MUST go through fileUploader.uploadFiles, not a
    // Promise.all of uploadFile. Parallel getUploadURL calls reuse the same
    // bearer/seq and trip the server's checkNonce replay protection
    // (lambdas/bearer.ts). The batch helper serializes the URL fetch and
    // PUTs to S3 in parallel.
    const startFileUploads = (newTempFiles, tempPartition, timestamp) => {
        const batchInputs = newTempFiles.map((tempFile, i) => ({
            file: tempFile.file,
            contentId: `temp_${i}_${timestamp}`,
        }));
        fileUploader
            .uploadFiles({
            files: batchInputs,
            partition: tempPartition,
            auth,
            cacheBusterStore,
            onFileComplete: (index, result) => {
                setTempFiles((prev) => {
                    if (index >= prev.length)
                        return prev;
                    const updated = [...prev];
                    updated[index] = Object.assign(Object.assign({}, updated[index]), { uploadProgress: result.success ? 100 : 0, uploadComplete: result.success, uploadError: result.success ? undefined : result.error });
                    return updated;
                });
            },
        })
            .then((results) => {
            const successCount = results.filter((r) => r.success).length;
            const errorCount = results.length - successCount;
            logger.log(LOG_PREFIX +
                `All uploads completed. Success: ${successCount}, Failed: ${errorCount}`);
            setUploadingFiles(false);
            setAllFilesUploaded(errorCount === 0);
        })
            .catch((uploadError) => {
            logger.error(LOG_PREFIX + "Upload error: " + uploadError);
            setErrorMessage("Upload failed: " + (uploadError.message || uploadError));
            setUploadingFiles(false);
        });
    };
    // Execute post save with reCAPTCHA token
    const doSaveWithToken = (token) => {
        try {
            // Prepare unified savePost request with reCAPTCHA token
            const savePostInput = {
                operation: "savePost",
                userId: auth.userId,
                channelOwnerId: channelOwnerId,
                channelId: channelId,
                postData: {
                    title: postTitle,
                    subtitle: postSubtitle,
                    text: postText,
                    link: postLink,
                    postId: postData.postId,
                    isEdit: true,
                },
                tempFiles: tempFiles.length > 0
                    ? tempFiles.map((tf) => (Object.assign({ tempPath: tf.tempPath, filename: tf.filename, fileType: tf.fileType }, (tf.videoMetadata && {
                        videoMetadata: tf.videoMetadata,
                    }))))
                    : [],
                bearer: auth.bearer,
                sequence: auth.sequence,
                recaptchaToken: token,
            };
            const callbacks = {
                onSuccess: (result) => {
                    if (result.bearer && result.sequence) {
                        auth.setBearerAndSequence(result.bearer, result.sequence, logger);
                    }
                    logger.log(LOG_PREFIX + "Post saved successfully");
                    // Increment cache buster after successful post edit
                    cacheBusterStore.incrementCacheBuster();
                    // Navigate back to post view
                    navigate(`/PostView?postId=${postId}&channelOwnerId=${channelOwnerId}&channelId=${channelId}`, { replace: true });
                },
                onError: (errorMessage) => {
                    setErrorMessage(errorMessage);
                    setSaving(false);
                },
            };
            savePostShared(savePostInput, callbacks, cacheBusterStore, logger);
        }
        catch (err) {
            setErrorMessage("Unexpected error during save: " + err.message);
            setSaving(false);
        }
    };
    // Save the edited post using unified savePost operation with reCAPTCHA
    const savePost = () => __awaiter(void 0, void 0, void 0, function* () {
        var _d, _e;
        setSaving(true);
        setErrorMessage(null);
        const grecaptcha = window.grecaptcha;
        if (grecaptcha &&
            ((_d = grecaptcha.enterprise) === null || _d === void 0 ? void 0 : _d.ready) &&
            ((_e = grecaptcha.enterprise) === null || _e === void 0 ? void 0 : _e.execute)) {
            grecaptcha.enterprise.ready(() => __awaiter(void 0, void 0, void 0, function* () {
                grecaptcha.enterprise
                    .execute("6LeMTvYqAAAAAEXBRStAkzbH267Azriet8mmhqMq", {
                    action: "submit",
                })
                    .then((token) => {
                    doSaveWithToken(token);
                });
            }));
        }
        else {
            doSaveWithToken("grecaptcha-not-loaded");
        }
    });
    const handleCancel = () => {
        navigate(`/PostView?postId=${postId}&channelOwnerId=${channelOwnerId}&channelId=${channelId}`);
    };
    // Simple validation effects
    useEffect(() => {
        if (postTitle && (postTitle.includes("<") || postTitle.includes("\n"))) {
            setPostTitleHelper(postTitle.includes("<")
                ? "Title cannot contain '<' character."
                : "Title cannot contain newline character.");
        }
        else {
            setPostTitleHelper(undefined);
        }
    }, [postTitle]);
    useEffect(() => {
        if (postSubtitle &&
            (postSubtitle.includes("<") || postSubtitle.includes("\n"))) {
            setPostSubtitleHelper(postSubtitle.includes("<")
                ? "Subtitle cannot contain '<' character."
                : "Subtitle cannot contain newline character.");
        }
        else {
            setPostSubtitleHelper(undefined);
        }
    }, [postSubtitle]);
    useEffect(() => {
        if (postText && postText.includes("<")) {
            setPostTextHelper("Text cannot contain '<' character.");
        }
        else {
            setPostTextHelper(undefined);
        }
    }, [postText]);
    useEffect(() => {
        if (postLink && !postLink.startsWith("https://")) {
            setPostLinkHelper("External Link URL must start with 'https://'");
        }
        else {
            setPostLinkHelper(undefined);
        }
    }, [postLink]);
    // Simple change tracking
    useEffect(() => {
        if (postData) {
            const titleChanged = postTitle !== (postData.title || "");
            const subtitleChanged = postSubtitle !== (postData.subtitle || "");
            const textChanged = postText !== (postData.text || "");
            const linkChanged = postLink !== (postData.link || "");
            const mediaChanged = tempFiles.length > 0;
            setHasChanges(titleChanged ||
                subtitleChanged ||
                textChanged ||
                linkChanged ||
                mediaChanged);
        }
    }, [postTitle, postSubtitle, postText, postLink, tempFiles.length, postData]);
    // Monitor temp files
    useEffect(() => {
        if (tempFiles.length > 0) {
            const allUploaded = tempFiles.every((tf) => tf.uploadComplete === true);
            const hasErrors = tempFiles.some((tf) => tf.uploadError);
            setAllFilesUploaded(allUploaded && !hasErrors);
        }
    }, [tempFiles]);
    // Build content
    let content;
    if (loading) {
        content = (_jsx(Card, Object.assign({ elevation: 10, sx: {
                backgroundColor: "#FFFFFF",
                padding: layout.cardPadding,
                margin: layout.cardMargin,
                textAlign: "center",
            } }, { children: _jsxs(CardContent, Object.assign({ sx: { padding: layout.cardContentPadding } }, { children: [_jsx(Typography, Object.assign({ variant: "h6" }, { children: "Loading Post" })), _jsx(Typography, Object.assign({ variant: "body1", sx: { margin: "10px 0 20px" } }, { children: "Loading post data for editing..." })), _jsx(LinearProgress, {})] })) })));
    }
    else if (errorMessage) {
        content = (_jsxs(Card, Object.assign({ elevation: 10, sx: {
                backgroundColor: "#FFEEEE",
                padding: layout.cardPadding,
                margin: layout.cardMargin,
                textAlign: "center",
            } }, { children: [_jsxs(CardContent, Object.assign({ sx: { padding: layout.cardContentPadding } }, { children: [_jsx(Typography, Object.assign({ variant: "h6", color: "error" }, { children: "ERROR" })), _jsx(Typography, Object.assign({ variant: "body1", sx: {
                                textAlign: "left",
                                padding: layout.cardContentPadding,
                                marginTop: "10px",
                            } }, { children: errorMessage }))] })), _jsx(CardActions, Object.assign({ sx: { display: "flex", justifyContent: "center" } }, { children: _jsx(Button, Object.assign({ variant: "contained", onClick: handleCancel, sx: { margin: "0.5em" } }, { children: "Go Back" })) }))] })));
    }
    else if (postData && channelOwnerId && channelId) {
        // Simple preview logic
        const hasVideo = tempFiles.some((f) => f.fileType === "mp4" || f.fileType === "mov");
        let previewMediaUrl = "";
        let previewIsVideo = false;
        // Priority 1: New files
        if (tempFiles.length > 0 || uploadingFiles) {
            if (hasVideo) {
                previewIsVideo = true; // Show video processing placeholder
            }
            else if (previewImageDataUrl) {
                previewMediaUrl = previewImageDataUrl;
                previewIsVideo = false;
            }
        }
        // Priority 2: Existing media (only if no new files)
        else if (existingMediaUrl) {
            previewMediaUrl = existingMediaUrl;
            previewIsVideo = existingIsVideo;
        }
        // Create preview header
        const previewHeader = postTitle || postSubtitle ? (_jsx(CardHeader, { title: postTitle, subheader: postSubtitle, avatar: _jsx(UserAvatar, { keyStr: "preview.avatar", userId: auth.userId || "", userName: auth.displayName || "", userNameVisible: false, auth: auth, onUserClicked: () => { } }), titleTypographyProps: {
                variant: "h6",
                margin: "0",
                marginRight: "18px",
                marginTop: postSubtitle ? "0" : "6px",
                marginBottom: postSubtitle ? "0" : "6px",
                padding: "0",
            }, subheaderTypographyProps: {
                variant: "body1",
                margin: "0",
                marginRight: "17px",
                padding: "0",
            }, sx: {
                margin: 0,
                padding: 0,
                marginLeft: "10px",
                marginRight: "10px",
            } })) : undefined;
        // Simple preview content
        let previewContent = null;
        if (previewMediaUrl && !previewIsVideo) {
            previewContent = (_jsx(ContentCard, { title: "", header: previewHeader, text: postText, mediaUrl: previewMediaUrl, mediaIsVideo: false, keyStr: `edit-post-preview-image-${forceRender}`, textColor: "black", backgroundColor: "#FFFFFF", maxMediaWidth: window.innerWidth - 100, maxMediaHeight: window.innerHeight - 400 }));
        }
        else if (hasVideo) {
            previewContent = (_jsx(ContentCard, { title: "", header: previewHeader, childComponent: _jsxs(Stack, Object.assign({ alignItems: "center", justifyContent: "center", sx: { width: "100%", padding: 2 } }, { children: [_jsx("img", { src: "/img/video-processing.jpg", alt: "Video Processing", style: {
                                maxWidth: "100%",
                                maxHeight: window.innerHeight - 500,
                                height: "auto",
                                borderRadius: "4px",
                            } }), _jsx(Typography, Object.assign({ variant: "body2", color: "text.secondary", sx: { mt: 1 } }, { children: "Video will be processed after upload" }))] })), keyStr: `edit-post-preview-video-${forceRender}`, textColor: "black", backgroundColor: "#FFFFFF", maxMediaWidth: window.innerWidth - 100, maxMediaHeight: window.innerHeight - 400 }));
        }
        else if (previewMediaUrl && previewIsVideo) {
            previewContent = (_jsx(ContentCard, { title: "", header: previewHeader, text: postText, mediaUrl: previewMediaUrl, mediaIsVideo: true, keyStr: `edit-post-preview-existing-video-${forceRender}`, textColor: "black", backgroundColor: "#FFFFFF", maxMediaWidth: window.innerWidth - 100, maxMediaHeight: window.innerHeight - 400 }));
        }
        else if (postTitle || postSubtitle || postText) {
            previewContent = (_jsx(ContentCard, { title: "", header: previewHeader, text: postText, keyStr: `edit-post-preview-text-${forceRender}`, textColor: "black", backgroundColor: "#FFFFFF" }));
        }
        // Main form
        const cardContent = (_jsxs(Stack, Object.assign({ style: { padding: "10px" } }, { children: [!saving && (_jsxs(_Fragment, { children: [_jsx(TextField, { fullWidth: true, label: "Title", size: "small", value: postTitle, onChange: (e) => setPostTitle(e.target.value), error: !!postTitleHelper, helperText: postTitleHelper, disabled: uploadingFiles, sx: { marginBottom: "8px" } }), _jsx(TextField, { fullWidth: true, label: "Subtitle", size: "small", value: postSubtitle, onChange: (e) => setPostSubtitle(e.target.value), error: !!postSubtitleHelper, helperText: postSubtitleHelper, disabled: uploadingFiles, sx: { marginBottom: "8px" } }), _jsx(TextField, { fullWidth: true, label: "Write your post here...", multiline: true, rows: 2, value: postText, onChange: (e) => setPostText(e.target.value), error: !!postTextHelper, helperText: postTextHelper, disabled: uploadingFiles, sx: { marginBottom: addLinkVisible ? "8px" : "3px" } }), addLinkVisible && (_jsx(TextField, { fullWidth: true, label: "External Link URL", size: "small", value: postLink, onChange: (e) => setPostLink(e.target.value), error: !!postLinkHelper, helperText: postLinkHelper, disabled: uploadingFiles, sx: { marginBottom: "8px" } }))] })), _jsxs(Stack, Object.assign({ direction: "row", width: "100%", sx: { marginTop: "5px" }, spacing: 1, style: { display: "flex", justifyContent: "center" } }, { children: [_jsx("input", { type: "file", accept: "image/jpeg,image/png,video/mp4,video/quicktime", multiple: true, ref: hiddenFileInput, style: { display: "none" }, onChange: handleFileSelection }), _jsx("canvas", { ref: canvasRef, style: { display: "none" } }), !saving && !uploadingFiles && (_jsxs(_Fragment, { children: [_jsx(Button, Object.assign({ variant: tempFiles.length > 0 ? "outlined" : "contained", endIcon: _jsx(AddAPhoto, {}), onClick: () => { var _a; return (_a = hiddenFileInput.current) === null || _a === void 0 ? void 0 : _a.click(); } }, { children: tempFiles.length > 0 ? "REPLACE MEDIA" : "+ PHOTOS/VIDEOS" })), _jsx(Button, Object.assign({ variant: postLink ? "outlined" : "contained", endIcon: _jsx(AddLink, {}), onClick: () => setAddLinkVisible(true) }, { children: "+ LINK" }))] }))] })), (uploadingFiles || (tempFiles.length > 0 && allFilesUploaded)) && (_jsxs(Stack, Object.assign({ spacing: 1, sx: { marginTop: "10px" } }, { children: [_jsx(Typography, Object.assign({ variant: "h6", color: allFilesUploaded ? "success.main" : "primary" }, { children: allFilesUploaded
                                ? "Files Uploaded - Ready to Save"
                                : "Uploading files..." })), tempFiles.map((tf, index) => (_jsxs(Stack, Object.assign({ spacing: 0.5 }, { children: [_jsxs(Typography, Object.assign({ variant: "caption", color: tf.uploadComplete
                                        ? "success.main"
                                        : tf.uploadError
                                            ? "error"
                                            : "text.secondary" }, { children: [tf.file.name, " ", tf.uploadComplete ? "✓" : tf.uploadError ? "✗" : "..."] })), !tf.uploadComplete && !tf.uploadError && _jsx(LinearProgress, {}), tf.uploadError && (_jsx(Typography, Object.assign({ variant: "caption", color: "error" }, { children: tf.uploadError })))] }), index)))] }))), saving && (_jsx(Card, Object.assign({ elevation: 0, sx: {
                        backgroundColor: "rgba(31, 0, 238, 0.04)",
                        padding: "4",
                        marginTop: "0.5em",
                    } }, { children: _jsxs(CardContent, Object.assign({ sx: { textAlign: "center" } }, { children: [_jsx(Typography, Object.assign({ variant: "h6", color: "primary", gutterBottom: true }, { children: "SAVING POST" })), _jsx(Typography, Object.assign({ variant: "body2", sx: { marginBottom: "15px" } }, { children: "Saving your changes..." })), _jsx(LinearProgress, { color: "primary", variant: "indeterminate", sx: { height: 8, borderRadius: 4 } })] })) }))), errorMessage && (_jsx(Card, Object.assign({ elevation: 0, sx: {
                        backgroundColor: "#FFEEEE",
                        padding: "4",
                        marginTop: "0.5em",
                    } }, { children: _jsxs(CardContent, { children: [_jsx(Typography, Object.assign({ variant: "h6", color: "error", gutterBottom: true }, { children: "ERROR" })), _jsx(Typography, Object.assign({ variant: "body2" }, { children: errorMessage }))] }) })))] })));
        // Validation check
        const isValid = !postTitleHelper &&
            !postSubtitleHelper &&
            !postTextHelper &&
            !postLinkHelper &&
            (postTitle.length > 0 || tempFiles.length > 0);
        const canSave = isValid && hasChanges;
        content = (_jsxs(Card, Object.assign({ sx: {
                backgroundColor: "#FFFFFF",
                color: "black",
                marginTop: "20px",
                marginBottom: "20px",
                width: "100%",
                boxShadow: "0 6px 20px rgba(0, 0, 0, 0.4)",
            } }, { children: [_jsx(CardHeader, { title: _jsx(Typography, Object.assign({ variant: "h6", sx: { fontWeight: 500 } }, { children: "Edit Post" })), sx: { textAlign: "center", paddingBottom: 0 } }), previewContent && (_jsxs(CardContent, Object.assign({ sx: {
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                    } }, { children: [_jsx(Typography, Object.assign({ variant: "h6", sx: {
                                marginBottom: "10px",
                                alignSelf: "flex-start",
                                width: "100%",
                            } }, { children: "Preview" })), _jsx("div", Object.assign({ style: {
                                width: "100%",
                                display: "flex",
                                justifyContent: "center",
                            } }, { children: previewContent }))] }))), _jsx(CardContent, { children: cardContent }), _jsxs(CardActions, Object.assign({ sx: {
                        display: "flex",
                        justifyContent: "center",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1,
                        paddingBottom: 2,
                    } }, { children: [_jsxs(Stack, Object.assign({ direction: "row", spacing: 1, sx: { justifyContent: "center" } }, { children: [_jsx(Button, Object.assign({ "aria-label": "Save Button", variant: "contained", color: "primary", endIcon: _jsx(SaveAlt, {}), onClick: savePost, disabled: !canSave ||
                                        saving ||
                                        uploadingFiles ||
                                        (tempFiles.length > 0 && !allFilesUploaded), sx: { minWidth: "150px" } }, { children: "Save Post" })), _jsx(Button, Object.assign({ variant: "outlined", onClick: handleCancel, disabled: saving || uploadingFiles, sx: { minWidth: "100px" } }, { children: "Cancel" }))] })), tempFiles.length > 0 && !allFilesUploaded && (_jsx(Typography, Object.assign({ variant: "caption", color: "text.secondary" }, { children: "Wait for uploads to complete..." }))), _jsxs(Typography, Object.assign({ variant: "caption", color: "text.secondary", sx: { textAlign: "center", marginTop: 1 } }, { children: ["This site is protected by reCAPTCHA and the Google", " ", _jsx(Link, Object.assign({ href: "https://policies.google.com/privacy", target: "_blank", rel: "noopener" }, { children: "Privacy Policy" })), " ", "and", " ", _jsx(Link, Object.assign({ href: "https://policies.google.com/terms", target: "_blank", rel: "noopener" }, { children: "Terms of Service" })), " ", "apply."] }))] }))] }), `edit-post-${postId}-${forceRender}`));
    }
    return (_jsx("div", Object.assign({ style: { width: "100%", padding: 0, margin: 0 }, id: "outer" }, { children: _jsxs(ThemeProvider, Object.assign({ theme: getTheme() }, { children: [_jsx(Heading, {}), _jsx(Container, Object.assign({ sx: {
                        backgroundColor: "#FFFFFF",
                        width: "100%",
                        padding: 0,
                        margin: 0,
                    }, maxWidth: false }, { children: content })), _jsx(Footer, {})] })) })));
};
